import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';

import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';

import {
  Calculator,
  Copy,
  Check,
  RotateCcw,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  BookOpen,
  Superscript,
  Radical,
  TrendingUp,
  Zap,
} from 'lucide-react';

import { exponentRules, radicalRules, exponentFAQs } from '../data/mockData';

// Superscript conversion helper
const toSuperscript = (num) => {
  const superscripts = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻', '.': '·' };
  return String(num).split('').map(c => superscripts[c] || c).join('');
};

// Subscript conversion helper
const toSubscript = (num) => {
  const subscripts = { '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉' };
  return String(num).split('').map(c => subscripts[c] || c).join('');
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Generate chart data for exponential function
const generateExponentChartData = (base, currentExponent, range = [-3, 5], steps = 100) => {
  const data = [];
  const step = (range[1] - range[0]) / steps;
  
  for (let x = range[0]; x <= range[1]; x += step) {
    const y = Math.pow(base, x);
    if (isFinite(y) && y < 1000 && y > -1000) {
      data.push({ 
        x: parseFloat(x.toFixed(2)), 
        y: parseFloat(y.toFixed(4)),
      });
    }
  }
  return data;
};

// Visual representation of exponent
const ExponentVisual = ({ base, exponent }) => {
  const result = Math.pow(base, exponent);
  const isValidResult = isFinite(result) && Math.abs(result) < 1e15;
  
  // Create visual blocks for small positive integer exponents
  const showBlocks = exponent > 0 && exponent <= 5 && Number.isInteger(exponent) && base > 0 && base <= 10;
  
  return (
    <div className="bg-muted/30 rounded-xl p-4 border">
      <div className="text-center mb-4">
        <div className="text-4xl font-bold text-foreground mb-2">
          {base}<sup className="text-2xl text-amber-500">{exponent}</sup>
          <span className="text-muted-foreground mx-3">=</span>
          <span className="text-emerald-500">
            {isValidResult ? (Math.abs(result) > 1e6 ? result.toExponential(4) : result.toLocaleString()) : 'Too large'}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          {base} multiplied by itself {exponent} time{exponent !== 1 ? 's' : ''}
        </p>
      </div>
      
      {showBlocks && (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {Array.from({ length: Math.abs(exponent) }).map((_, i) => (
            <React.Fragment key={i}>
              <div className="w-12 h-12 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center font-bold text-amber-600">
                {base}
              </div>
              {i < Math.abs(exponent) - 1 && (
                <span className="text-muted-foreground text-xl">×</span>
              )}
            </React.Fragment>
          ))}
          <span className="text-2xl text-muted-foreground mx-2">=</span>
          <div className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 font-bold text-emerald-600 text-xl">
            {result}
          </div>
        </div>
      )}
      
      {exponent < 0 && (
        <div className="mt-4 p-3 bg-background rounded-lg text-center">
          <p className="text-sm text-muted-foreground">
            Negative exponent means reciprocal:
          </p>
          <p className="font-mono text-foreground mt-1">
            {base}<sup>{exponent}</sup> = 1 / {base}<sup>{Math.abs(exponent)}</sup> = 1 / {Math.pow(base, Math.abs(exponent))} = {result.toFixed(6)}
          </p>
        </div>
      )}
      
      {exponent === 0 && (
        <div className="mt-4 p-3 bg-background rounded-lg text-center">
          <p className="text-sm text-muted-foreground">
            Any non-zero number raised to the power of 0 equals 1
          </p>
        </div>
      )}
    </div>
  );
};

// Radical Visual representation
const RadicalVisual = ({ radicand, index }) => {
  const result = Math.pow(radicand, 1 / index);
  const isExactRoot = Number.isInteger(result) || Math.abs(result - Math.round(result)) < 0.0001;
  const displayResult = isExactRoot ? Math.round(result) : result.toFixed(6);
  
  // Check if result can be verified
  const verification = Math.pow(isExactRoot ? Math.round(result) : result, index);
  
  return (
    <div className="bg-muted/30 rounded-xl p-4 border">
      <div className="text-center mb-4">
        <div className="text-4xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
          {index !== 2 && <span className="text-xl text-cyan-500">{index}</span>}
          <span className="text-cyan-500">√</span>
          <span className="border-t-2 border-cyan-500 px-2">{radicand}</span>
          <span className="text-muted-foreground mx-3">=</span>
          <span className="text-emerald-500">{displayResult}</span>
        </div>
        <p className="text-sm text-muted-foreground">
          What number, multiplied by itself {index} times, equals {radicand}?
        </p>
      </div>
      
      {isExactRoot && (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {Array.from({ length: index }).map((_, i) => (
            <React.Fragment key={i}>
              <div className="w-12 h-12 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center font-bold text-cyan-600">
                {displayResult}
              </div>
              {i < index - 1 && (
                <span className="text-muted-foreground text-xl">×</span>
              )}
            </React.Fragment>
          ))}
          <span className="text-2xl text-muted-foreground mx-2">=</span>
          <div className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 font-bold text-emerald-600 text-xl">
            {radicand}
          </div>
        </div>
      )}
      
      {!isExactRoot && (
        <div className="mt-4 p-3 bg-background rounded-lg text-center">
          <p className="text-sm text-muted-foreground">
            This is an irrational number (non-perfect {index === 2 ? 'square' : index === 3 ? 'cube' : 'root'})
          </p>
          <p className="font-mono text-foreground mt-1 text-sm">
            Verification: {displayResult}<sup>{index}</sup> ≈ {verification.toFixed(4)}
          </p>
        </div>
      )}
    </div>
  );
};

function ExponentCalculator() {
  const [mode, setMode] = useState('exponent'); // 'exponent' or 'radical'
  const [base, setBase] = useState(2);
  const [exponent, setExponent] = useState(3);
  const [radicand, setRadicand] = useState(16);
  const [radicalIndex, setRadicalIndex] = useState(2);
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  const exponentResult = useMemo(() => {
    return Math.pow(base, exponent);
  }, [base, exponent]);

  const radicalResult = useMemo(() => {
    return Math.pow(radicand, 1 / radicalIndex);
  }, [radicand, radicalIndex]);

  const chartData = useMemo(() => {
    return generateExponentChartData(base, exponent);
  }, [base, exponent]);

  const handleReset = useCallback(() => {
    if (mode === 'exponent') {
      setBase(2);
      setExponent(3);
    } else {
      setRadicand(16);
      setRadicalIndex(2);
    }
  }, [mode]);

  const formatResult = (val) => {
    if (!isFinite(val)) return 'undefined';
    if (Math.abs(val) > 1e10) return val.toExponential(6);
    if (Number.isInteger(val)) return val.toLocaleString();
    return val.toFixed(6);
  };

  // Quick exponent examples
  const quickExponents = [
    { base: 2, exp: 3 },
    { base: 2, exp: 10 },
    { base: 10, exp: 2 },
    { base: 5, exp: 3 },
    { base: 3, exp: 4 },
    { base: 2, exp: -1 },
  ];

  // Quick radical examples
  const quickRadicals = [
    { rad: 16, idx: 2 },
    { rad: 27, idx: 3 },
    { rad: 81, idx: 4 },
    { rad: 100, idx: 2 },
    { rad: 125, idx: 3 },
    { rad: 64, idx: 3 },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Superscript className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Exponent & Radical Simplifier</h1>
            <p className="text-muted-foreground">Calculate powers, roots, and visualize exponential functions</p>
          </div>
        </div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Calculator Panel */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:sticky lg:top-24 lg:self-start"
        >
          <Card className="rounded-2xl p-6 border bg-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-amber-500" />
                <span className="font-semibold text-foreground">Calculator</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={handleReset} 
                title="Reset"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            {/* Mode Toggle */}
            <Tabs value={mode} onValueChange={setMode} className="mb-6">
              <TabsList className="w-full">
                <TabsTrigger value="exponent" className="flex-1 gap-2">
                  <Superscript className="w-4 h-4" />
                  Exponent
                </TabsTrigger>
                <TabsTrigger value="radical" className="flex-1 gap-2">
                  <Radical className="w-4 h-4" />
                  Radical
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <AnimatePresence mode="wait">
              {mode === 'exponent' ? (
                <motion.div
                  key="exponent"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  {/* Base Input */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Base (a)</Label>
                    <Input
                      type="number"
                      value={base}
                      onChange={(e) => setBase(parseFloat(e.target.value) || 0)}
                      className="border text-xl font-mono h-14"
                      placeholder="2"
                    />
                  </div>

                  {/* Exponent Input */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Exponent (n)</Label>
                    <Input
                      type="number"
                      value={exponent}
                      onChange={(e) => setExponent(parseFloat(e.target.value) || 0)}
                      className="border text-xl font-mono h-14"
                      placeholder="3"
                    />
                  </div>

                  {/* Quick Examples */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                    <div className="flex flex-wrap gap-2">
                      {quickExponents.map((ex, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className={`text-xs rounded-lg transition-all ${base === ex.base && exponent === ex.exp ? 'bg-amber-500 text-white border-amber-500' : ''}`}
                          onClick={() => { setBase(ex.base); setExponent(ex.exp); }}
                        >
                          {ex.base}{toSuperscript(ex.exp)}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Result */}
                  <div className="pt-4 border-t border-border">
                    <Label className="text-sm font-medium text-foreground mb-2 block">Result</Label>
                    <div className="rounded-xl p-4 bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-muted-foreground">{base}{toSuperscript(exponent)}</span>
                        <CopyButton text={formatResult(exponentResult)} />
                      </div>
                      <span className="font-mono text-2xl font-bold text-foreground">
                        {formatResult(exponentResult)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="radical"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  {/* Radicand Input */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Radicand (number under root)</Label>
                    <Input
                      type="number"
                      value={radicand}
                      onChange={(e) => setRadicand(parseFloat(e.target.value) || 0)}
                      className="border text-xl font-mono h-14"
                      placeholder="16"
                    />
                  </div>

                  {/* Index Input */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Root Index (2 = square root, 3 = cube root)</Label>
                    <Input
                      type="number"
                      value={radicalIndex}
                      onChange={(e) => setRadicalIndex(parseInt(e.target.value) || 2)}
                      min="2"
                      className="border text-xl font-mono h-14"
                      placeholder="2"
                    />
                  </div>

                  {/* Quick Examples */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                    <div className="flex flex-wrap gap-2">
                      {quickRadicals.map((ex, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className={`text-xs rounded-lg transition-all ${radicand === ex.rad && radicalIndex === ex.idx ? 'bg-cyan-500 text-white border-cyan-500' : ''}`}
                          onClick={() => { setRadicand(ex.rad); setRadicalIndex(ex.idx); }}
                        >
                          {ex.idx !== 2 && <span className="text-[10px]">{ex.idx}</span>}√{ex.rad}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Result */}
                  <div className="pt-4 border-t border-border">
                    <Label className="text-sm font-medium text-foreground mb-2 block">Result</Label>
                    <div className="rounded-xl p-4 bg-cyan-500/10 border border-cyan-500/30">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-muted-foreground">
                          {radicalIndex !== 2 && <span>{radicalIndex}</span>}√{radicand}
                        </span>
                        <CopyButton text={formatResult(radicalResult)} />
                      </div>
                      <span className="font-mono text-2xl font-bold text-foreground">
                        {formatResult(radicalResult)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>

        {/* Center/Right Column - Visualization & Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Visualization Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Interactive Visualization
                  <Badge variant="secondary" className="ml-2 text-xs">Real-time</Badge>
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Visual Representation */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    {mode === 'exponent' ? 'Exponent Breakdown' : 'Radical Breakdown'}
                  </h3>
                  {mode === 'exponent' ? (
                    <ExponentVisual base={base} exponent={exponent} />
                  ) : (
                    <RadicalVisual radicand={radicand} index={radicalIndex} />
                  )}
                </div>

                {/* Graph */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    {mode === 'exponent' ? `f(x) = ${base}ˣ Graph` : 'Relationship Graph'}
                  </h3>
                  <div className="rounded-xl p-4 h-[220px] bg-muted/30 border">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis
                          dataKey="x"
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                          domain={[0, 100]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                          formatter={(value) => [value.toFixed(4), `${base}ˣ`]}
                        />
                        <ReferenceLine 
                          x={exponent} 
                          stroke="#f59e0b" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                        />
                        {isFinite(exponentResult) && exponentResult < 100 && (
                          <ReferenceDot 
                            x={exponent} 
                            y={exponentResult} 
                            r={6} 
                            fill="#f59e0b" 
                            stroke="#fff"
                          />
                        )}
                        <Line
                          type="monotone"
                          dataKey="y"
                          stroke="#f59e0b"
                          dot={false}
                          strokeWidth={2}
                          animationDuration={300}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    The highlighted point shows your current calculation
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Step-by-Step Solution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-amber-500" />
                Step-by-Step Solution
              </h2>

              <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                {mode === 'exponent' ? (
                  <>
                    <AccordionItem value="step-1" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                          <span className="font-medium">Identify the expression</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <p className="text-muted-foreground">
                            We have <span className="font-mono text-foreground font-semibold bg-secondary px-2 py-0.5 rounded">{base}{toSuperscript(exponent)}</span>
                          </p>
                          <div className="bg-secondary/50 rounded-lg p-4">
                            <p className="text-sm">Base: <span className="font-bold text-amber-500">{base}</span></p>
                            <p className="text-sm">Exponent: <span className="font-bold text-amber-500">{exponent}</span></p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                          <span className="font-medium">Apply the calculation</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          {exponent >= 0 && exponent <= 5 && Number.isInteger(exponent) ? (
                            <>
                              <p className="text-muted-foreground">Multiply {base} by itself {exponent} times:</p>
                              <div className="bg-secondary/50 rounded-lg p-4 font-mono">
                                {exponent === 0 ? (
                                  <p>{base}⁰ = 1 (any non-zero number to power 0 is 1)</p>
                                ) : (
                                  <p>
                                    {Array.from({ length: exponent }).map((_, i) => (
                                      <span key={i}>
                                        {base}{i < exponent - 1 ? ' × ' : ''}
                                      </span>
                                    ))} = {formatResult(exponentResult)}
                                  </p>
                                )}
                              </div>
                            </>
                          ) : exponent < 0 ? (
                            <>
                              <p className="text-muted-foreground">Negative exponent means reciprocal:</p>
                              <div className="bg-secondary/50 rounded-lg p-4 font-mono space-y-2">
                                <p>{base}{toSuperscript(exponent)} = 1 / {base}{toSuperscript(Math.abs(exponent))}</p>
                                <p>= 1 / {Math.pow(base, Math.abs(exponent))}</p>
                                <p>= {formatResult(exponentResult)}</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="text-muted-foreground">Using the power formula:</p>
                              <div className="bg-secondary/50 rounded-lg p-4 font-mono">
                                <p>{base}{toSuperscript(exponent)} = {formatResult(exponentResult)}</p>
                              </div>
                            </>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3" className="border-0">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                          <span className="font-medium">Final result</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11">
                          <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/30">
                            <p className="text-lg font-mono">
                              <span className="text-muted-foreground">{base}{toSuperscript(exponent)} = </span>
                              <span className="text-2xl font-bold text-emerald-600">{formatResult(exponentResult)}</span>
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </>
                ) : (
                  <>
                    <AccordionItem value="step-1" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                          <span className="font-medium">Identify the radical</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <p className="text-muted-foreground">
                            We need to find the {radicalIndex === 2 ? 'square' : radicalIndex === 3 ? 'cube' : `${radicalIndex}th`} root of {radicand}
                          </p>
                          <div className="bg-secondary/50 rounded-lg p-4">
                            <p className="text-sm">Radicand: <span className="font-bold text-cyan-500">{radicand}</span></p>
                            <p className="text-sm">Index: <span className="font-bold text-cyan-500">{radicalIndex}</span></p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                          <span className="font-medium">Find the root</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <p className="text-muted-foreground">
                            Ask: "What number multiplied by itself {radicalIndex} times equals {radicand}?"
                          </p>
                          <div className="bg-secondary/50 rounded-lg p-4 font-mono">
                            <p>{radicalIndex !== 2 && <span>{radicalIndex}</span>}√{radicand} = {radicand}^(1/{radicalIndex}) = {formatResult(radicalResult)}</p>
                          </div>
                          {Number.isInteger(radicalResult) && (
                            <p className="text-sm text-emerald-600">
                              ✓ This is a perfect {radicalIndex === 2 ? 'square' : radicalIndex === 3 ? 'cube' : 'power'}!
                            </p>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3" className="border-0">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                          <span className="font-medium">Verify & final result</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <div className="bg-secondary/50 rounded-lg p-4 font-mono text-sm">
                            <p>Verification: {formatResult(radicalResult)}{toSuperscript(radicalIndex)} = {formatResult(Math.pow(radicalResult, radicalIndex))}</p>
                          </div>
                          <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/30">
                            <p className="text-lg font-mono">
                              <span className="text-muted-foreground">{radicalIndex !== 2 && <span>{radicalIndex}</span>}√{radicand} = </span>
                              <span className="text-2xl font-bold text-emerald-600">{formatResult(radicalResult)}</span>
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </>
                )}
              </Accordion>
            </Card>
          </motion.div>

          {/* Rules Section */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              {mode === 'exponent' ? 'Exponent Rules' : 'Radical Rules'}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {(mode === 'exponent' ? exponentRules : radicalRules).map((rule, i) => (
                <div key={i} className="bg-muted/30 rounded-xl p-4">
                  <span className="text-xs text-muted-foreground block mb-1">{rule.name}</span>
                  <code className="font-mono text-sm text-foreground block mb-2">{rule.formula}</code>
                  <p className="text-xs text-muted-foreground">{rule.description}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Common Mistakes */}
          <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Common Mistakes to Avoid
            </h2>
            <ul className="space-y-3">
              {[
                'Confusing (aᵐ)ⁿ = aᵐˣⁿ with aᵐ × aⁿ = aᵐ⁺ⁿ',
                'Forgetting that a⁰ = 1 (not 0) for any non-zero a',
                'Thinking √(a + b) = √a + √b (this is WRONG!)',
                'Forgetting negative exponents mean reciprocals: a⁻ⁿ = 1/aⁿ',
                'Not recognizing that even roots of negative numbers are not real',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* FAQ Section */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {exponentFAQs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-medium text-foreground">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>

          {/* Real World Applications */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
            <ul className="space-y-3">
              {[
                'Compound interest calculations use exponential growth (A = P(1+r)ⁿ)',
                'Computer science uses powers of 2 for memory and data storage (KB, MB, GB)',
                'Population growth and decay models rely on exponential functions',
                'Sound intensity (decibels) and earthquake magnitude (Richter scale) use logarithms',
                'Square roots are essential in the Pythagorean theorem and distance calculations',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ArrowRight className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default ExponentCalculator;
